import type { RPCIdentifier } from '../rpc/bridge-api-rpc-message';

import type { CommandResult } from './bridge-api-command-types';

/**
 * Represents a command invocation with support for cancellation.
 *
 * @description Yielded in response to sending a comment to provide a consistent API, inspired by patterns
 * from {@link globalThis.fetch} - an opinionated API which does not leak internal details.
 */
export class CommandInvocation<R extends CommandResult> {
	constructor(
		private readonly invocationId: RPCIdentifier,
		private readonly resultPromise: Promise<R | undefined>,
		private readonly cancelCallback: (id: RPCIdentifier) => boolean,
		private readonly cancelGroupCallback: (groupId: string) => number,
	) {}

	/**
	 * Get the result of the command invocation.
	 *
	 * @description Returns a promise that resolves to the command result, or undefined if cancelled.
	 *
	 * @returns Promise that resolves to the command result, or undefined if cancelled
	 */
	result(): Promise<R | undefined> {
		return this.resultPromise;
	}

	/**
	 * Cancel this specific command invocation.
	 *
	 * @description Attempts to cancel the command if it's still buffered or in-flight.
	 * If the command has already completed, this will have no effect.
	 *
	 * @returns true if the command was found and cancelled, false if it was already completed/cancelled
	 */
	cancel(): boolean {
		return this.cancelCallback(this.invocationId);
	}

	/**
	 * Cancel all commands in a specific group.
	 *
	 * @description Cancels all buffered commands that share the same groupId.
	 * This is useful for cancelling related commands that were sent as part of the same operation.
	 *
	 * @param groupId The group identifier of commands to cancel
	 * @returns number of commands that were cancelled
	 */
	cancelGroup(groupId: string): number {
		return this.cancelGroupCallback(groupId);
	}
}
